/**
 * BookCover - ISBN から書影を表示する。
 *
 * バックエンド `/api/calil/cover?isbn={isbn}` を通して画像バイトを取得する。
 * バックエンド側で NDL → openBD → Google Books の順に試し、Cache-Control で
 * 長時間キャッシュされる。フロントは単に <img> を貼るだけで、404 (書影なし)
 * や失敗時は onError でプレースホルダを表示する。
 *
 * バックエンド経由にする理由:
 *   - NDL はホットリンク保護で 403 (Referer / UA チェック) を返すためブラウザ直叩き不可
 *   - openBD の summary.cover が指す Amazon / 出版社 CDN もホットリンクを弾くことがある
 *   - サーバー側で適切なヘッダ (User-Agent, Referer) を付ければバイパスできる
 */

import { BookOpen } from 'lucide-react'
import { useEffect, useState } from 'react'

import { resolveDirectUrl } from '@/api/client'
import { cn } from '@/lib/utils'

type Props = {
  isbn: string | null
  className?: string
}

const coverUrl = (isbn: string): string => resolveDirectUrl(`/api/calil/cover?isbn=${encodeURIComponent(isbn)}`)

const Placeholder = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-center rounded-sm border border-border/40 bg-muted/30 text-muted-foreground',
      className,
    )}
    aria-hidden
  >
    <BookOpen className="size-5 opacity-50" />
  </div>
)

export const BookCover = ({ isbn, className }: Props) => {
  const [failed, setFailed] = useState(false)

  // ISBN が変わったら失敗状態をリセット
  useEffect(() => {
    setFailed(false)
  }, [isbn])

  if (!isbn || failed) {
    return <Placeholder className={className} />
  }

  return (
    <img
      src={coverUrl(isbn)}
      alt=""
      loading="lazy"
      decoding="async"
      className={cn('shrink-0 rounded-sm border border-border/40 bg-muted/20 object-cover', className)}
      onError={() => setFailed(true)}
    />
  )
}
